import { useQuery } from 'react-query';
import {
  performanceReportingApiUrl,
  fetcher,
  handleError,
  createParamString,
  ServerErrorResponse,
} from 'data_sources/constants';
import type { ReviewWorkflowDetail } from '../_types';

export const getReviewWorkflow = async (
  id: number | string,
  organizationId?: string | null,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<ReviewWorkflowDetail> => {
  const baseUrl = `${performanceReportingApiUrl()}/performance/review-workflow/v1/review-workflows/${id}`;
  const queryParams = createParamString({ organizationId });
  const url = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
  try {
    const response = await fetcher({ url, method: 'GET', options: { signal } });
    if (response?.errorDetails) {
      if (onError) onError(response);
      throw new Error(JSON.stringify(response));
    }
    return response;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    } else if (onError && error instanceof Error) {
      handleError(error, { onError });
    }
    throw error;
  }
};

export default function useReviewWorkflow(
  id: number | string | undefined,
  organizationId?: string | null,
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  return useQuery<ReviewWorkflowDetail, ServerErrorResponse>(
    ['review-workflow', id, organizationId],
    async ({ signal }) => getReviewWorkflow(id!, organizationId, signal, onError),
    { enabled: !!id, ...options }
  );
}
